/**
 * Thin wrapper around @capgo/capacitor-social-login.
 *
 * Lazy-imports the plugin so the web bundle never pulls native code.
 * All exports are safe to call from any environment — they no-op or
 * throw clearly on non-native platforms.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = (): boolean => Capacitor.isNativePlatform();
export const isIOS = (): boolean => Capacitor.getPlatform() === "ios";
export const isAndroid = (): boolean => Capacitor.getPlatform() === "android";

let socialLoginInitialised = false;

async function getPlugin() {
  const mod = await import("@capgo/capacitor-social-login");
  return mod.SocialLogin;
}

/**
 * Initialise the SocialLogin plugin with the client IDs from
 * capacitor.config.ts. Idempotent — safe to call multiple times.
 */
export async function initSocialLogin(): Promise<void> {
  if (socialLoginInitialised || !isNative()) return;
  const SocialLogin = await getPlugin();

  // Capgo's initialize() requires an explicit config object at runtime;
  // capacitor.config.ts values are not auto-consumed by the plugin's JS
  // initializer. Keep these in sync with capacitor.config.ts.
  // The Web/server client ID is the ID-token audience Supabase validates.
  const webClientId = "250212903724-pfvg8nh04v6o1sih0n980vjbh54sf7p4.apps.googleusercontent.com";

  await SocialLogin.initialize({
    google: {
      iOSClientId: "250212903724-4tm7ekfl3kls5vt394al3fjgk9pqhhq5.apps.googleusercontent.com",
      iOSServerClientId: webClientId,
      webClientId,
    },
    // IMPORTANT: do NOT pass redirectUrl here. Capgo's iOS AppleProvider
    // POSTs the Apple auth code to redirectUrl when non-empty, which 404s
    // against our SPA. Native iOS SIWA returns the ID token directly —
    // no post-auth HTTP request is needed.
    apple: isIOS()
      ? {
          clientId: "com.like2fit.app",
        }
      : undefined,
  });

  socialLoginInitialised = true;
}

export interface NativeGoogleResult {
  idToken: string;
}

/**
 * Android-only path. iOS uses the Supabase OAuth flow (Path B2) via
 * src/lib/oauthBrowser.ts; calling this on iOS would mint an ID token
 * with the iOS bundle audience that Lovable Cloud's GoTrue cannot
 * validate.
 */
export async function signInWithGoogleNative(): Promise<NativeGoogleResult> {
  if (!isNative() || isIOS()) {
    throw new Error("signInWithGoogleNative is Android-only");
  }
  await initSocialLogin();
  const SocialLogin = await getPlugin();
  const res = await SocialLogin.login({ provider: "google", options: {} });

  // Capgo's response shape: { provider: 'google', result: { idToken, ... } }
  const idToken = (res as { result?: { idToken?: string } })?.result?.idToken;
  if (!idToken) {
    throw new Error("Google sign-in did not return an ID token");
  }
  return { idToken };
}

export interface NativeAppleResult {
  idToken: string;
  /** Apple-issued nonce; undefined on returning sign-ins. */
  nonce?: string;
  /** Only present on the very first sign-in for this user. */
  email: string | null;
  /** Only present on the very first sign-in for this user. */
  fullName: { givenName: string | null; familyName: string | null } | null;
}

export async function signInWithAppleNative(): Promise<NativeAppleResult> {
  if (!isIOS()) {
    throw new Error("signInWithAppleNative is iOS-only");
  }
  await initSocialLogin();
  const SocialLogin = await getPlugin();

  const nonce = crypto.randomUUID();
  const res = await SocialLogin.login({
    provider: "apple",
    options: { scopes: ["email", "fullName"], nonce },
  });

  const result = (res as unknown as { result?: Record<string, unknown> })?.result ?? {};
  const idToken = (result as { idToken?: string }).idToken;
  if (!idToken) {
    throw new Error("Apple sign-in did not return an ID token");
  }
  const profile = (result as {
    profile?: { email?: string | null; givenName?: string | null; familyName?: string | null };
  }).profile;
  const email = profile?.email ?? null;
  const fullName =
    profile && (profile.givenName || profile.familyName)
      ? { givenName: profile.givenName ?? null, familyName: profile.familyName ?? null }
      : null;

  return { idToken, nonce, email, fullName };
}

/** Logs the user out of both Google and Apple on the native side. */
export async function signOutNative(): Promise<void> {
  if (!isNative()) return;
  try {
    const SocialLogin = await getPlugin();
    await Promise.allSettled([
      SocialLogin.logout({ provider: "google" }),
      isIOS() ? SocialLogin.logout({ provider: "apple" }) : Promise.resolve(),
    ]);
  } catch {
    // Best-effort; native logout failures shouldn't block Supabase signOut.
  }
}
